/**
 * CommentUtils
 *
 * Pure helpers for the content comments sheet. Filed here (not inline in the
 * hooks) per conventions.md § "New responsibility -> new owner file": the
 * availability predicate is a ported gating condition and the two builders are
 * ported write-shape mappings — business logic by conventions.md §
 * "Flutter parity", so they belong below the presentation layer and stay if
 * their first caller is deleted.
 */

import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import type { ContentReplyCommentInfoEntity } from '@/domain/entities/comment/contentReplyCommentInfoEntity';
import type { MediaInfoEntity } from '@/domain/entities/info/mediaInfoEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { MediaType } from '@/domain/enums/mediaType';

/**
 * Whether the video-reply (add-media) action is available for the currently
 * selected reply target — parity with Flutter's `_videoReplyIsAvailable()`
 * (comments_bottom_sheet.dart:101-116):
 *
 * ```dart
 * Comment? selectedComment = _commentToReplyNotifier.value;
 * if (selectedComment != null) {
 *   return widget.media.firebaseId == _currentUser.firebaseId ||
 *       selectedComment.text.contains("@${_currentUser.username}");
 * }
 * return false;
 * ```
 *
 * A reply target must be selected, AND either the current user owns the
 * content OR the selected comment's text mentions the current user. The
 * mention check is a plain substring `contains`, NOT a word-boundary match —
 * ported literally (so `@ann` also matches a comment mentioning `@anna`).
 *
 * The one deliberate divergence from the Dart above is the non-empty-username
 * guard on the mention arm (see the inline comment in the body): the Dart is
 * buggy for an empty `username`, and that bug is fixed in Flutter in the same
 * change rather than mirrored here.
 *
 * The explicit `!!media.firebaseId` guard is web-only bookkeeping, not a
 * behaviour change: `UserMediaEntity.firebaseId` is optional on web while
 * Flutter's `User.firebaseId` is a non-nullable `late String`
 * (lib/data/models/user.dart:26), so on Flutter a missing content owner id
 * (`String?`) already compares false. Without the guard, `undefined ===
 * undefined` could report ownership on web.
 */
export function isVideoReplyAvailable(
  media: UserMediaEntity,
  currentUser: UserEntity | null,
  selectedComment: CommentEntity | null,
): boolean {
  if (!selectedComment || !currentUser) return false;

  const isContentOwner =
    !!media.firebaseId && media.firebaseId === currentUser.firebaseId;

  // The mention arm requires a real username: `UserRemapper` defaults an absent
  // `username` to '' (userRemapper.ts:20), and `@${''}` would degenerate to a
  // bare '@' that matches ANY comment carrying a mention — unlocking the video
  // reply on content the user does not own. Flutter has the same hole
  // (`User.username` defaults to "", user.dart:91 / user.g.dart:83) and is
  // fixed alongside this; do not port the bug (conventions.md § "Handling
  // obvious Flutter bugs").
  const mentionsCurrentUser =
    !!currentUser.username && selectedComment.text.includes(`@${currentUser.username}`);

  return isContentOwner || mentionsCurrentUser;
}

/**
 * Build the placeholder `MediaInfoEntity` written onto the reply comment the
 * moment the publish is confirmed, before the transcode has produced anything
 * — parity with comments_bottom_sheet.dart:895-902:
 *
 * ```dart
 * MediaInfo()
 *   ..firebaseId = AppAuthenticationService.getCurrentFirebaseUser()!.uid
 *   ..relativePath = "" ..relativeThumbnailPath = "" ..type = 1
 *   ..aspectRatio = widget.media.aspectRatio;
 * ```
 *
 * Two easy-to-get-wrong facts:
 *  - `aspectRatio` comes from the CONTENT being commented on
 *    (`widget.media.aspectRatio`), never from the uploaded reply video — the
 *    reply video's own ratio is stamped later by the server.
 *  - the empty `path` / `thumbnail` are what drive the "Processing"
 *    placeholder (`CommentMediaThumbnail.tsx:45`, `isProcessing =
 *    !mediaInfo.thumbnail`) until the transcode Cloud Function stamps the real
 *    values onto the comment doc and the live listener re-renders.
 *
 * `allowUnlock: false` is Flutter's `MediaInfo` constructor default
 * (lib/data/models/media_info.dart:33-36) — the Dart cascade above never sets
 * it, so the emitted doc carries `false`. Web's `MediaInfoEntity` already uses
 * the wire names `path` / `thumbnail` for Flutter's
 * `@JsonKey(name: 'path') relativePath` / `@JsonKey(name: 'thumbnail')
 * relativeThumbnailPath` (media_info.dart:6-40).
 */
export function buildVideoReplyPlaceholderMediaInfo(
  media: UserMediaEntity,
  firebaseUserId: string,
): MediaInfoEntity {
  return {
    firebaseId: firebaseUserId,
    path: '',
    thumbnail: '',
    type: MediaType.VIDEO,
    aspectRatio: media.aspectRatio,
    allowUnlock: false,
  };
}

/**
 * Build the `ContentReplyCommentInfoEntity` that rides the `transcodeUserVideo`
 * payload so the server can stamp the finished media back onto the reply
 * comment — parity with comments_bottom_sheet.dart:903-913:
 *
 * ```dart
 * ..commentId = newCommentId
 * ..replyId  = selectedComment.parent != null ? selectedComment.id : null
 * ..parentId = selectedComment.parent != null ? selectedComment.parent!.id : selectedComment.id
 * ..mediaPath = widget.media.relativePath
 * ..replyUsername = selectedComment.username;
 * ```
 *
 * Web-vs-Flutter discriminator: Flutter branches on the transient
 * `selectedComment.parent != null` link. Web's `CommentEntity.parent` is never
 * populated — `CommentRemapper.toEntity` sets `parent: null`
 * (commentRemapper.ts:55) and the comments hook nests children under
 * `children` instead — so the persisted `parentId` is used as the
 * discriminator, which expresses the same intent: a child comment carries a
 * `parentId`, a root comment does not.
 */
export function buildContentReplyCommentInfo(
  commentId: string,
  selectedComment: CommentEntity,
  media: UserMediaEntity,
): ContentReplyCommentInfoEntity {
  return {
    commentId,
    replyId: selectedComment.parentId ? selectedComment.id : undefined,
    parentId: selectedComment.parentId ?? selectedComment.id,
    mediaPath: media.relativePath,
    replyUsername: selectedComment.username,
  };
}
